import React, { useState } from "react";
import * as api from "../services/projectApi";
import { STATUS_TONE } from "./format";

// On-demand AI performance analysis (spec §11). Grounded, read-only — the
// backend enforces that the model never claims an ad action was taken.
export default function AiAnalysisPanel({ projectId, rangeParams }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      setResult(await api.analyzePerformance(projectId, rangeParams));
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const a = result?.analysis;
  const tone = a ? STATUS_TONE[a.health?.status] || "muted" : "muted";

  return (
    <div className="proj-panel ai-analysis">
      <div className="proj-progress-head">
        <h4 style={{ margin: 0 }}>AI analysis</h4>
        <button className="proj-btn proj-btn-primary" onClick={run} disabled={loading}>
          {loading ? "Analyzing…" : a ? "Re-analyze" : "Analyze performance"}
        </button>
      </div>

      {error && <div className="proj-alert">{error}</div>}
      {loading && <p className="proj-muted">Reading this project's data and writing a grounded analysis — about 30–60 seconds.</p>}
      {!loading && !a && !error && (
        <p className="proj-muted">Get a plain-language read on how this project is doing, grounded only in its data.</p>
      )}

      {a && (
        <div className="ai-report">
          <p className="ai-summary">{a.summary}</p>

          {a.health && (
            <div className={`ai-health tone-${tone}`}>
              <span className={`status-chip tone-${tone}`}>{a.health.status}</span>
              <span className="proj-muted">{a.health.explanation}</span>
            </div>
          )}

          <Section title="What's working" items={a.positiveFindings} tone="good" />
          <Section title="Risks" items={a.risks} tone="bad" />
          <KpiSection items={a.kpiAnalysis} />
          <Section title="Strategy alignment" items={a.strategyAlignment} tone="muted" />
          <Section title="Questions to investigate" items={a.questionsToInvestigate} tone="warn" />
          <Section title="Recommended next steps" items={a.recommendedNextSteps} tone="accent" numbered />

          <p className="ai-meta proj-muted">
            Generated {result.generatedAt ? new Date(result.generatedAt).toLocaleString() : ""} · read-only, no ad changes made.
          </p>
        </div>
      )}
    </div>
  );
}

function Section({ title, items, tone, numbered }) {
  if (!items || items.length === 0) return null;
  const List = numbered ? "ol" : "ul";
  return (
    <div className="ai-section">
      <span className={`ai-section-title tone-${tone}`}>{title}</span>
      <List className="ai-list">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </List>
    </div>
  );
}

function KpiSection({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="ai-section">
      <span className="ai-section-title tone-muted">KPI notes</span>
      <ul className="ai-list">
        {items.map((k, i) => (
          <li key={i}>
            <strong>{k.metric}</strong>
            {k.status ? ` — ${k.status}` : ""}: {k.comment}
          </li>
        ))}
      </ul>
    </div>
  );
}
